import React from 'react';
import Icons from '../../icons';
import {
  ORDER_STATUSES_DISPLAY,
  ORDER_TYPES,
  STEP_TYPE,
  TIME_FORMAT,
  DAYTIME_FORMAT,
  ORDER_PICKUP_STATUS_COMPLETED,
  ORDER_DELIVERY_STATUS_COMPLETED,
} from '../../constants';
import './StopClicked.scss';
import { useSelector } from 'react-redux';
import { selectAllOrders } from '../../modules/orders/stores/slice';
import moment from 'moment';
import { Button, Popconfirm, Tag } from 'antd';

// `.StopClicked` sits at z-index 99999999 (StopClicked.scss) so it beats the
// map. AntD renders a Popconfirm in a body portal at ~1000, i.e. BEHIND that
// popup — Seb saw the "Cancel this stop?" box half-hidden under it (Sep 7
// 2026). The popup can't host the overlay itself (overflow: scroll would clip
// it), so the overlays go one step above the popup instead.
const POPUP_OVERLAY_Z_INDEX = 100000000;
import { useTranslation } from 'react-i18next';

// OVO-153 — statuses where the driver has already started/completed the
// stop: unassigning is blocked (mirrors the backend guard).
const UNASSIGN_BLOCKED_STATUSES = ['PICKUP_ARRIVED', 'COLLECTED', 'IN_TRANSIT', 'DELIVERY_ARRIVED', 'COMPLETED'];

export default function StopClicked({ item, solutions, onStepClick, routes, onUnassignStop, onCancelStop }) {
  const { t } = useTranslation('routes');

  const orders = useSelector(selectAllOrders);

  const closeStep = () => {
    onStepClick && onStepClick({});
  };

  // Vehicle / start / end click: the id encodes the route as
  // `${route.id}-start` or `${route.id}-end`. Render a small info card
  // with route + driver context — mirrors the pickup/delivery info card
  // shape so it lives in the same place visually.
  if (item.type === 'vehicle') {
    const routeIdMatch = String(item.id || '').match(/^(\d+)-(start|end)$/);
    const routeId = routeIdMatch ? Number(routeIdMatch[1]) : null;
    const which = routeIdMatch ? routeIdMatch[2] : 'start';
    const route = (routes || []).find((r) => r.id === routeId);
    if (!route) {
      // Route not in the current solution set — nothing to show. Close the
      // popup state so the user isn't left with a "stuck" selection.
      closeStep();
      return null;
    }

    const isStart = which === 'start';
    const address = isStart ? route.startAddress : route.endAddress || route.startAddress;

    // Vehicle (fleet) — included on the day-plan route via schedulerForm.fleet.
    const fleet = route.schedulerForm?.fleet;
    const vehicleLabel = fleet ? `${fleet.name || '-'}${fleet.tag ? ` · ${fleet.tag}` : ''}` : '-';

    // Clicked-end-specific times. The planned boundary is a plain "HH:mm"
    // wall-clock string; the actual shift start/end is a full UTC ISO datetime
    // (from the driver's logs). Render an ISO value in the viewer's local
    // timezone (date + time); leave a plain "HH:mm" as-is (no tz to convert).
    const plannedTime = isStart ? route.shiftStart : route.shiftEnd;
    const actualRaw = isStart ? route.actualShiftStart : route.actualShiftEnd;
    const actualIsIso = typeof actualRaw === 'string' && moment(actualRaw, moment.ISO_8601, true).isValid();
    const actualTime = actualRaw ? (actualIsIso ? moment(actualRaw).format(DAYTIME_FORMAT) : actualRaw) : null;

    return (
      <div className="StopClicked">
        <div className="info-header">
          <Icons type="InfoWindowCar" className="info-icon" color={route.color || '#4F8CE0'} />
          <span className="info-id" style={{ color: route.color || '#4F8CE0' }}>
            {isStart ? t('stopClicked.routeStart') : t('stopClicked.routeEnd')}
          </span>
          <a className="btn-close" onClick={closeStep}>
            x
          </a>
        </div>
        <div className="info-window">
          <div className="info-content">
            <div className="info-detail">
              <strong>{t('stopClicked.driver') || 'Driver'}</strong> <span>{route.driver?.name || '-'}</span>
            </div>
            <div className="info-detail">
              <strong>{t('stopClicked.vehicle')}</strong> <span>{vehicleLabel}</span>
            </div>
            <div className="info-detail">
              <strong>{isStart ? t('stopClicked.startLocation') : t('stopClicked.endLocation')}</strong>{' '}
              <span>{address || '-'}</span>
            </div>
            <div className="info-detail">
              <strong>{t('stopClicked.plannedTime')}</strong> <span>{plannedTime || '-'}</span>
            </div>
            <div className="info-detail">
              <strong>{t('stopClicked.actualTime')}</strong> <span>{actualTime || '-'}</span>
            </div>
            <div className="info-detail">
              <strong>{t('stopClicked.status')}</strong> <span>{route.status || '-'}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const keyItems = Object.keys(solutions).find((key) =>
    solutions[key]?.some((step) => step.id === item.id && step.type.toLowerCase() === item.type.toLowerCase()),
  );

  const items = solutions[keyItems] || [];
  const step = items?.find((i) => i.id === item.id && i.type.toLowerCase() === item.type.toLowerCase());
  if (!step) return null;

  // Co-located visits — other stops at the EXACT same coordinate (e.g. the
  // depot the bike reloads at between capacity batches). The map merges these
  // into one "1·4·7" pin; list them here so each visit stays reachable. The
  // stop number is the 1-based position in the solution's step list (matches
  // the map badge + timeline).
  const coLocatedVisits = items
    .map((i, idx) => ({ visit: i, number: idx + 1 }))
    .filter(
      ({ visit }) =>
        Number(visit?.lat).toFixed(6) === Number(step?.lat).toFixed(6) &&
        Number(visit?.lng).toFixed(6) === Number(step?.lng).toFixed(6),
    );

  const hdStepClick = (e, step) => {
    e.stopPropagation();
    e.preventDefault();
    onStepClick && onStepClick({ id: step.id, type: step.type, orderId: step.orderId, orderIds: step.orderIds });
  };

  const completedTime = (stepData) => {
    // For delivery, use first orderId from orderIds array; for pickup, use first orderId
    const orderIds = stepData.orderIds || (stepData.orderId ? [stepData.orderId] : []);
    const orderId = orderIds[0];
    const type = stepData.type;
    const logs = stepData?.logs || [];
    const order = orders.find((o) => o.id === orderId);
    if (!order) return null;
    if (ORDER_PICKUP_STATUS_COMPLETED.includes(order?.pickupStatus) && type === STEP_TYPE.pickup) {
      const completedLogs = logs.filter((l) => ORDER_PICKUP_STATUS_COMPLETED.includes(l.status));
      const completedLog = completedLogs[completedLogs.length - 1];
      return completedLog?.createdAt;
    }
    if (ORDER_DELIVERY_STATUS_COMPLETED.includes(order?.status) && type === STEP_TYPE.delivery) {
      const completedLogs = logs.filter((l) => ORDER_DELIVERY_STATUS_COMPLETED.includes(l.status));
      const completedLog = completedLogs[completedLogs.length - 1];
      return completedLog?.createdAt;
    }
    return null;
  };

  // Stacked "Prévu / Réel" (planned / actual) time cell — same shape as the
  // RouteDetail stops table (renderStopTime), so a stop reads identically in
  // both places. Both values are already "HH:mm" strings here.
  const renderPlannedActual = (planned, actual) => {
    if (!planned && !actual) return '-';
    return (
      <div style={{ lineHeight: 1.35 }}>
        {planned && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {t('plannedLabel')} {planned}
          </div>
        )}
        {actual && (
          <div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('actualLabel')} </span>
            {actual}
          </div>
        )}
      </div>
    );
  };

  const getPickupNames = (orderIds) => {
    const names = orderIds.map((orderId) => {
      const order = orders.find((o) => o.id === orderId);
      return order?.pickupName;
    });
    const uniqueNames = [...new Set(names)];
    return uniqueNames.map((name, index) => (
      <span key={index + name}>
        {name}
        {index < uniqueNames.length - 1 && <br />}
      </span>
    ));
  };

  // OVO-010 — when the step covers multiple orders at the same address
  // (multi-pickup or multi-delivery merge), the popup header used to show
  // ONE of the order codes picked at random, which was misleading. Show a
  // generic "Multi-delivery / Multi-pickup (N orders)" label instead.
  const isMulti = (step.orderIds?.length || 0) > 1;
  const multiLabel = step.type === STEP_TYPE.delivery ? t('stopClicked.multiDelivery') : t('stopClicked.multiPickup');

  // Resolve each merged orderId to its order code so we can render a list
  // of clickable links underneath the multi-label header (previously the
  // user only saw the generic label with no way to jump to any of the
  // covered orders).
  const multiOrderLinks = isMulti
    ? step.orderIds
        .map((oid) => {
          const ord = orders.find((o) => o.id === oid);
          return ord ? { id: oid, orderCode: ord.orderCode } : null;
        })
        .filter(Boolean)
    : [];

  const headerContent = (
    <div className="info-header">
      <Icons type="InfoWindowCar" className="info-icon" color={step.color} />
      {isMulti ? (
        <span className="info-id" style={{ color: step.color }}>
          {multiLabel}{' '}
          <span style={{ opacity: 0.7, fontWeight: 400 }}>
            ({t('stopClicked.multiCount', { count: step.orderIds.length })})
          </span>
        </span>
      ) : (
        <a
          className="info-id"
          style={{ color: step.color }}
          href={`${window.location.origin}/admin/orders/${step.orderCode}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {step.orderCode}
        </a>
      )}
      <a className="btn-close" onClick={closeStep}>
        x
      </a>
    </div>
  );

  const steps =
    step.type === STEP_TYPE.delivery
      ? items.filter((i) => {
          // For delivery, find all pickups that have any orderId matching any orderId in delivery's orderIds
          const deliveryOrderIds = step.orderIds || (step.orderId ? [step.orderId] : []);
          return i.type === STEP_TYPE.pickup && deliveryOrderIds.some((orderId) => i.orderIds?.includes(orderId));
        })
      : items.filter((i) => {
          // For pickup, find deliveries that have any orderId matching any orderId in pickup's orderIds
          const pickupOrderIds = step.orderIds || [];
          return i.type === STEP_TYPE.delivery && pickupOrderIds.some((orderId) => i.orderIds?.includes(orderId));
        });

  // OVO-153 — unassign straight from the popup. Eligible when the stop is on
  // a real route and NO contained order has been started/completed by the
  // driver (merged stops unassign all their orders at once). Works on
  // STARTED routes too — that's the point: no re-optimization, the driver
  // just sees one fewer step.
  const stepOrders = (step.orderIds || []).map((oid) => orders.find((o) => o.id === oid)).filter(Boolean);
  // Scheduled = the step was found under a driver's solutions key. The
  // scheduled step objects don't carry routeId — only the unscheduled ones
  // do (as the literal 'unscheduled') — so key membership is the signal.
  const isScheduledStep = keyItems != null && keyItems !== 'unscheduled';
  const canUnassign =
    typeof onUnassignStop === 'function' &&
    isScheduledStep &&
    stepOrders.length > 0 &&
    stepOrders.every((o) => !UNASSIGN_BLOCKED_STATUSES.includes(o.status));

  const infoWindow = (
    <div className="info-window">
      <div className="info-content">
        {coLocatedVisits.length > 1 && (
          <div className="info-detail colocated-visits">
            <strong>{t('stopClicked.visitsHere')}</strong>
            <div className="visit-chips">
              {coLocatedVisits.map(({ visit, number }) => {
                const isCurrent = visit.id === step.id && visit.type === step.type;
                return (
                  <a
                    key={`${visit.id}-${visit.type}`}
                    className={isCurrent ? 'visit-chip active' : 'visit-chip'}
                    style={
                      isCurrent
                        ? { background: step.color, borderColor: step.color, color: '#fff' }
                        : { borderColor: step.color, color: step.color }
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isCurrent) return;
                      onStepClick &&
                        onStepClick({
                          id: visit.id,
                          type: visit.type,
                          orderId: visit.orderId,
                          orderIds: visit.orderIds,
                        });
                    }}
                  >
                    {number}
                  </a>
                );
              })}
            </div>
          </div>
        )}
        {isMulti && multiOrderLinks.length > 0 && (
          <div className="info-detail multi-order-links">
            <strong>{t('stopClicked.orders')}</strong>
            <div className="orders-list">
              {multiOrderLinks.map((o, i) => (
                <React.Fragment key={o.id}>
                  {i > 0 && ', '}
                  <a
                    href={`${window.location.origin}/admin/orders/${o.orderCode}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: step.color }}
                  >
                    {o.orderCode}
                  </a>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
        <div className="info-detail">
          <strong>{t('stopClicked.type')}</strong> <span>{step?.type}</span>
        </div>
        {step.type === STEP_TYPE.pickup && (
          <div className="info-detail">
            <strong>{t('stopClicked.pickupName')}</strong> <span>{getPickupNames(step.orderIds)}</span>
          </div>
        )}
        {step.type === STEP_TYPE.delivery && (
          <div className="info-detail">
            <strong>{t('stopClicked.deliveryName')}</strong> <span>{step?.name}</span>
          </div>
        )}

        <div className="info-detail">
          <strong>{t('stopClicked.location')}</strong> <span>{step?.address}</span>
        </div>
        <div className="info-detail">
          <strong>{t('stopClicked.partner')}</strong> <span>{step?.partner || '-'}</span>
        </div>
        <div className="info-detail">
          <strong>{t('stopClicked.orderTypes')}</strong> <span>{step?.orderTypes?.join(', ')}</span>
        </div>
        {/* OVO-143 — pickup/delivery time windows (the constraint behind a
            "Time window too tight" unscheduled reason). Shown when set. */}
        {(step?.pickupTimeStart || step?.pickupTimeEnd) && (
          <div className="info-detail">
            <strong>{t('stopClicked.pickupWindow')}</strong>{' '}
            <span>
              {step?.pickupTimeStart || '–'} – {step?.pickupTimeEnd || '–'}
            </span>
          </div>
        )}
        {(step?.deliveryTimeStart || step?.deliveryTimeEnd) && (
          <div className="info-detail">
            <strong>{t('stopClicked.deliveryWindow')}</strong>{' '}
            <span>
              {step?.deliveryTimeStart || '–'} – {step?.deliveryTimeEnd || '–'}
            </span>
          </div>
        )}
        <div className="info-detail">
          <strong>{t('stopClicked.ticketNames')}</strong> <span>{step?.ticketNames?.join(', ')}</span>
        </div>
        {step.type !== STEP_TYPE.pickup && (
          <div className="info-detail">
            <strong>{t('stopClicked.status')}</strong>{' '}
            <span>{ORDER_STATUSES_DISPLAY[step?.orderStatus] || step?.orderStatus}</span>
          </div>
        )}
        {step.type === STEP_TYPE.delivery && (
          <div className="info-detail">
            <strong>{t('stopClicked.packages')}</strong> <span>{step?.orderIds?.length || '-'}</span>
          </div>
        )}
        <div className="info-detail">
          <strong>{t('stopClicked.arrivalTime')}</strong>{' '}
          <span>{renderPlannedActual(step?.plannedArrivalTime, step?.arrivalTime)}</span>
        </div>
        <div className="info-detail">
          <strong>{t('stopClicked.finishTime')}</strong>{' '}
          <span>{renderPlannedActual(step?.plannedFinishTime, step?.finishTime)}</span>
        </div>
        {completedTime(step) && (
          <div className="info-detail">
            <strong>{t('stopClicked.completedTime')}</strong>{' '}
            <span>{moment(completedTime(step)).format(TIME_FORMAT)}</span>
          </div>
        )}
        {step?.orderType === ORDER_TYPES.SERVICE && (
          <div className="info-detail">
            <Tag color="orange">{t('service')}</Tag>
          </div>
        )}
        {step?.collectedItems?.length > 0 && (
          <div className="info-detail collected-items-section">
            <strong>
              {t('stopClicked.collectedItems')} ({step.collectedItems.length})
            </strong>
            <div className="collected-items-list">
              {step.collectedItems.map((item) => {
                const unit = item.partnerItemType?.unitOfMeasure || 'kg';
                return (
                  <div key={item.id} className="collected-item">
                    <span className="item-name">{item.partnerItemType?.name || item.description || t('item')}</span>
                    {item.scannedCode && <span className="item-code">{item.scannedCode}</span>}
                    {item.quantity > 1 && <span className="item-qty">x{item.quantity}</span>}
                    {item.weight != null && (
                      <span className="item-weight">
                        {item.weight} {unit}
                      </span>
                    )}
                    {item.partner?.name && <span className="item-partner">{item.partner.name}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {step?.unscheduledReason && (
          <div className="info-detail">
            <strong>{t('stopClicked.reason')}</strong>
            <span style={{ color: 'red' }}>{step?.unscheduledReason}</span>
          </div>
        )}
        <div className="info-detail">
          {step.type === STEP_TYPE.delivery && steps?.length > 0 && (
            <>
              {' '}
              <strong>{t('stopClicked.pickups')}: </strong>
              <div className="steps">
                {steps.map((step) => {
                  return (
                    <div className="step" key={step.id} onClick={(e) => hdStepClick(e, step)}>
                      <div className="pickup" style={{ border: `2px solid ${step.color}`, color: step.color }}>
                        {step?.index}
                      </div>
                      <b>{getPickupNames(step.orderIds)}</b>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          {step.type === STEP_TYPE.pickup && (
            <>
              {' '}
              <strong>{t('stopClicked.deliveries')}: </strong>
              <div className="steps">
                {steps.map((step) => {
                  const stepKey =
                    step.orderIds?.length > 0 ? `${step.orderIds[0]}_${step.type}` : `${step.id}_${step.type}`;
                  return (
                    <div className="step" key={stepKey} onClick={(e) => hdStepClick(e, step)}>
                      <div className="delivery" style={{ border: `2px solid ${step.color}`, color: step.color }}>
                        {step?.index}
                      </div>
                      <b>{step.name}</b>
                      <a className="status">{ORDER_STATUSES_DISPLAY[step?.pickupStatus] || step?.pickupStatus}</a>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        {canUnassign && (
          <div className="info-detail" style={{ marginTop: 8 }}>
            <Popconfirm
              title={t('stopClicked.unassignConfirmTitle')}
              description={t('stopClicked.unassignConfirmDesc', { count: stepOrders.length })}
              okText={t('stopClicked.unassignStop')}
              okButtonProps={{ danger: true }}
              overlayStyle={{ zIndex: POPUP_OVERLAY_Z_INDEX }}
              onConfirm={() => onUnassignStop(stepOrders.map((o) => o.orderCode))}
            >
              <Button danger size="small" block>
                {t('stopClicked.unassignStop')}
                {stepOrders.length > 1 ? ` (${stepOrders.length})` : ''}
              </Button>
            </Popconfirm>
          </div>
        )}
        {/* "Cancel stop" (Seb, Aug 2026) — third exit from a planned stop:
            Unassign → back to the pool; this → terminal CANCELLED, gone from
            the unscheduled list too. Same eligibility as Unassign (the
            backend re-checks). */}
        {canUnassign && typeof onCancelStop === 'function' && (
          <div className="info-detail" style={{ marginTop: 8 }}>
            <Popconfirm
              title={t('stopClicked.cancelConfirmTitle')}
              description={t('stopClicked.cancelConfirmDesc', { count: stepOrders.length })}
              okText={t('stopClicked.cancelStop')}
              okButtonProps={{ danger: true }}
              overlayStyle={{ zIndex: POPUP_OVERLAY_Z_INDEX }}
              onConfirm={() => onCancelStop(stepOrders.map((o) => o.orderCode))}
            >
              <Button size="small" block>
                {t('stopClicked.cancelStop')}
                {stepOrders.length > 1 ? ` (${stepOrders.length})` : ''}
              </Button>
            </Popconfirm>
          </div>
        )}
      </div>
    </div>
  );
  return (
    <div className="StopClicked">
      {headerContent}
      {infoWindow}
    </div>
  );
}
